import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../components/auth/AuthProvider';
import { AuthPageErrorBoundary } from '@/components/auth/AuthPageErrorBoundary';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function ResetPasswordPage() {
  return (
    <AuthPageErrorBoundary pageName="reset password">
      <ResetPasswordContent />
    </AuthPageErrorBoundary>
  );
}

function ResetPasswordContent() {
  const { error, resetPassword } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isResetSuccessful, setIsResetSuccessful] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset validation errors
    setValidationError(null);
    
    // Simple validation
    if (password.length < 8) {
      setValidationError('Password must be at least 8 characters long');
      return;
    }
    
    if (password !== confirmPassword) {
      setValidationError('Passwords do not match');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Call the resetPassword method from auth context with the token
      const result = await resetPassword(password, token || undefined);
      
      console.log('Reset password result:', result);
      
      if (result.error) {
        setValidationError(result.error.message);
      } else {
        // Password reset successful
        console.log('Password reset successful');
        setIsResetSuccessful(true);
        
        // Auto-redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login', { 
            state: { fromPasswordReset: true },
            replace: true 
          });
        }, 3000);
      }
    } catch (error) {
      console.error('Password reset failed:', error);
      setValidationError('Password reset failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // If no token is provided in the URL, show an error
  if (!token) {
    return (
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-xl">Invalid Reset Link</CardTitle>
          <CardDescription>
            The password reset link is invalid or has expired.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4 items-center">
            <Button asChild className="w-full">
              <Link to="/forgot-password">Request New Reset Link</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show success screen after password reset
  if (isResetSuccessful) {
    return (
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-xl">Password Reset Successful! 🎉</CardTitle>
          <CardDescription>
            Your password has been successfully updated
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4 items-center">
            <div className="p-4 rounded-lg bg-green-50 border border-green-200 text-center w-full">
              <p className="text-sm text-green-800 font-medium mb-2">
                Password reset completed successfully!
              </p>
              <p className="text-xs text-green-700">
                You can now log in with your new password. You'll be redirected to the login page automatically.
              </p>
            </div>
            <div className="flex flex-col gap-2 w-full">
              <Button 
                className="w-full"
                onClick={() => navigate('/login', { 
                  state: { fromPasswordReset: true },
                  replace: true 
                })}
              >
                Login Now
              </Button>
              <Button asChild variant="outline" className="w-full">
                <Link to="/dashboard">Go to Dashboard</Link>
              </Button>
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Redirecting to login in 3 seconds...
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="text-center">
        <CardTitle className="text-xl">Reset Your Password</CardTitle>
        <CardDescription>
          Enter a new password for your account
        </CardDescription>
      </CardHeader>
      <CardContent>
        {(error || validationError) && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error || validationError}</AlertDescription>
          </Alert>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="grid gap-6">
            <div className="grid gap-3">
              <Label htmlFor="password">New Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground">
                Password must be at least 8 characters long
              </p>
            </div>
            <div className="grid gap-3">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            <Button 
              type="submit" 
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Resetting...' : 'Reset Password'}
            </Button>
            <div className="text-center text-sm">
              <Link to="/login" className="underline underline-offset-4">
                Back to login
              </Link>
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
